import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useTourBuilder } from '../context/TourBuilderContext';
import ProfessionalSearchBar from './FuturisticSearchBar';

const GOLD = '#B8975A';

const GlobalSearchModal = () => {
  const { isOpen, closeBuilder } = useTourBuilder();
  const searchRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const t = setTimeout(() => {
        if (searchRef.current) searchRef.current.handleExternalTrigger();
      }, 120);
      return () => clearTimeout(t);
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') closeBuilder(); };
    if (isOpen) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, closeBuilder]);

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        @keyframes gModalIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes gModalSlide {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .g-modal-wrap {
          position: fixed; inset: 0; z-index: 10020;
          background: rgba(8,14,26,0.92);
          backdrop-filter: blur(10px);
          animation: gModalIn 0.3s ease both;
          /* Full-height scroll container so the drawer panel has room */
          overflow-y: auto;
          overflow-x: hidden;
          /* Override the search bar's drawer to use absolute inside us */
        }
        /* ── Critical: re-scope the drawer so it fills our modal,
              not the browser viewport ── */
        /* Re-scope drawer to fill the modal, not the viewport */
        .g-modal-wrap .drawer-panel {
          position: absolute !important;
          top: 0 !important;
          right: 0 !important;
          /* On desktop: wider panel so form fields don't feel cramped */
          width: 100% !important;
          max-width: 520px !important;
          height: 100% !important;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
        }
        /* Make the form body fill remaining space so no scroll is needed */
        .g-modal-wrap .drawer-panel > div:last-child {
          flex: 1 !important;
          overflow-y: visible !important;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
        /* Give the form itself more breathing room */
        .g-modal-wrap .drawer-field {
          padding-top: 16px !important;
          padding-bottom: 16px !important;
          font-size:17px !important;
        }
        .g-modal-wrap .drawer-cta {
          padding-top: 20px !important;
          padding-bottom: 20px !important;
        }
        /* Space out form fields */
        .g-modal-wrap form.space-y-6 {
          gap: 28px !important;
        }
        .g-modal-wrap > .g-modal-inner {
          position: relative;
          min-height: 100vh;
        }
        .g-modal-header {
          width: 100%; max-width: 900px; margin: 0 auto;
          display: flex; align-items: center; justify-content: space-between;
          padding: 28px 16px 20px;
          position: sticky; top: 0; z-index: 10;
          /* Dark bg so header stays readable as user scrolls */
          background: rgba(8,14,26,0.95);
          backdrop-filter: blur(8px);
          animation: gModalSlide 0.4s cubic-bezier(0.22,1,0.36,1) 0.05s both;
        }
        .g-modal-body {
          width: 100%; max-width: 900px; margin: 0 auto;
          padding: 0 16px 60px;
          animation: gModalSlide 0.4s cubic-bezier(0.22,1,0.36,1) 0.1s both;
        }
        .g-modal-hint {
          text-align: center; margin-top: 20px;
          font-family: 'Jost', sans-serif; font-size:13px; font-weight: 300;
          color: rgba(255,255,255,0.18); letter-spacing: 0.15em;
        }

        /* ── Drawer interior overrides for modal context ── */

        /* Wider, taller drawer header area */
        .g-modal-wrap .drawer-panel > div:first-child {
          padding: 40px 40px 28px !important;
        }
        /* Selection summary */
        .g-modal-wrap .drawer-panel > div:nth-child(2) {
          padding: 20px 40px !important;
        }
        /* Form body — no internal scroll, vertically spaced */
        .g-modal-wrap .drawer-panel .flex-1 {
          padding: 32px 40px 40px !important;
          overflow-y: visible !important;
        }
        /* Larger form inputs */
        .g-modal-wrap .drawer-field {
          font-size:17px !important;
          padding: 14px 0 14px 36px !important;
        }
        /* More space between form rows */
        .g-modal-wrap form > div + div {
          margin-top: 28px;
        }
        /* Bigger submit button */
        .g-modal-wrap .drawer-cta {
          padding: 20px 32px !important;
          font-size:15px !important;
          letter-spacing:0.16em !important;
          margin-top: 36px !important;
        }
        /* Footer phone link */
        .g-modal-wrap .drawer-panel > div:last-child {
          padding: 20px 40px !important;
        }
        .g-close-btn {
          width: 40px; height: 40px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          border: 1px solid rgba(255,255,255,0.15);
          background: transparent; cursor: pointer;
          color: rgba(255,255,255,0.4);
          transition: all 0.25s;
        }
        .g-close-btn:hover { border-color: ${GOLD}; color: ${GOLD}; }
      `}</style>

      <div className="g-modal-wrap" onClick={(e) => { if (e.target.classList.contains('g-modal-wrap')) closeBuilder(); }}>
        <div className="g-modal-inner">

          {/* Header */}
          <div className="g-modal-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '28px', height: '1px', background: GOLD }} />
              <div>
                <p style={{
                  fontFamily: "'Jost',sans-serif", fontSize: '13px', fontWeight: 600,
                  letterSpacing: '0.2em', textTransform: 'uppercase', color: GOLD,
                  lineHeight: 1, marginBottom: '5px',
                }}>
                  Safari Planner
                </p>
                <p style={{
                  fontFamily: "'Cormorant Garamond',serif", fontSize: '27px',
                  fontStyle: 'normal', fontWeight: 300, color: '#fff', lineHeight: 1,
                }}>
                  Begin your journey
                </p>
              </div>
            </div>
            <button className="g-close-btn" onClick={closeBuilder} aria-label="Close">
              <X size={16} strokeWidth={1.5} />
            </button>
          </div>

          {/* Search bar */}
          <div className="g-modal-body">
            <ProfessionalSearchBar ref={searchRef} onClose={closeBuilder} />
            <p className="g-modal-hint">
              Select destination → set dates → enter your details to begin
            </p>
          </div>

        </div>
      </div>
    </>
  );
};

export default GlobalSearchModal;